<template>
  <el-dialog
    title="正式分析留痕"
    :visible="visible"
    width="92%"
    top="4vh"
    custom-class="formal-receipt-dialog"
    :close-on-click-modal="false"
    @open="handleOpen"
    @close="handleClose"
  >
    <div class="receipt-topbar">
      <div class="receipt-identity">
        <span>结论编号</span>
        <code>{{ conclusionId || '未记录' }}</code>
        <el-button
          v-if="conclusionId"
          type="text"
          size="mini"
          icon="el-icon-document-copy"
          @click="copyConclusionId"
        >复制</el-button>
      </div>
      <div class="readonly-badge"><i class="el-icon-lock"></i>只读查看</div>
    </div>

    <el-tabs v-model="view" class="receipt-tabs" @tab-click="handleTabClick">
      <el-tab-pane label="摘要" name="summary" />
      <el-tab-pane label="高级留痕" name="advanced" />
    </el-tabs>

    <div v-loading="loading" class="receipt-body">
      <el-alert
        v-if="error"
        :title="error"
        type="warning"
        :closable="false"
        show-icon
        class="receipt-alert"
      />

      <template v-else-if="receipt && view === 'summary'">
        <section class="availability-panel" :class="availabilityTone">
          <div class="availability-icon">
            <i :class="availabilityIcon"></i>
          </div>
          <div class="availability-copy">
            <span>本次留痕状态</span>
            <strong>{{ availabilityHeadline }}</strong>
            <p>{{ availabilityDescription }}</p>
          </div>
          <el-tag :type="availabilityTagType" effect="plain">
            {{ statusText(summary.completeness) }}
          </el-tag>
        </section>

        <section class="summary-status-grid">
          <div v-for="item in summaryItems" :key="item.key" class="summary-status-item">
            <div class="status-icon" :class="item.tone"><i :class="item.icon"></i></div>
            <div>
              <span>{{ item.label }}</span>
              <strong>{{ item.value }}</strong>
              <p>{{ item.desc }}</p>
            </div>
          </div>
        </section>

        <el-card shadow="never" class="receipt-card">
          <div slot="header" class="card-header">
            <div>
              <strong>分析信息</strong>
              <p>普通查看只保留判断留痕是否可用所需的信息。</p>
            </div>
          </div>
          <div class="info-grid">
            <div class="info-cell">
              <span>分析类型</span>
              <strong>{{ valueText(summary.analysis_type) }}</strong>
            </div>
            <div class="info-cell">
              <span>分析模式</span>
              <strong>{{ valueText(summary.analysis_mode) }}</strong>
            </div>
            <div class="info-cell">
              <span>分析时点</span>
              <strong>{{ formatBeijing(summary.analysis_as_of) }}</strong>
            </div>
            <div class="info-cell">
              <span>正式留痕</span>
              <strong>{{ statusText(summary.formal_trace_status) }}</strong>
            </div>
          </div>

          <el-alert
            v-if="missingFields.length"
            :title="'有 ' + missingFields.length + ' 项信息未完整记录，可在高级留痕中查看字段明细。'"
            type="warning"
            :closable="false"
            show-icon
            class="inner-alert"
          />
        </el-card>

        <el-card shadow="never" class="receipt-card">
          <div slot="header" class="card-header">
            <div>
              <strong>发布与回读</strong>
              <p>确认结论是否已发布，并能从正式记录中重新读取。</p>
            </div>
            <el-tag :type="statusTagType(publication.status)" size="small" effect="plain">
              {{ statusText(publication.status) }}
            </el-tag>
          </div>

          <div class="publication-grid">
            <div>
              <span>发布时间</span>
              <strong>{{ formatBeijing(publication.published_at) }}</strong>
            </div>
            <div>
              <span>完成时间</span>
              <strong>{{ formatBeijing(publication.completed_at) }}</strong>
            </div>
            <div>
              <span>回读状态</span>
              <strong>{{ statusText(publication.readback_status) }}</strong>
            </div>
          </div>

          <el-alert
            v-if="hasFailureReason"
            :title="'未完整原因：' + valueText(publication.failure_reason)"
            type="warning"
            :closable="false"
            show-icon
            class="inner-alert"
          />
        </el-card>
      </template>

      <template v-else-if="receipt && view === 'advanced'">
        <el-card shadow="never" class="receipt-card technical-card">
          <div slot="header" class="card-header">
            <div>
              <strong>技术引用</strong>
              <p>供需要进一步核对链路和契约的管理员查看。</p>
            </div>
            <el-tag size="small" effect="plain">{{ receipt.readonly ? '只读契约' : '状态待确认' }}</el-tag>
          </div>

          <div class="technical-grid">
            <div>
              <span>契约版本</span>
              <code>{{ valueText(receipt.contract_version) }}</code>
            </div>
            <div>
              <span>Pack ID</span>
              <code>{{ valueText(traceRefs.pack_id) }}</code>
            </div>
            <div>
              <span>Payload ID</span>
              <code>{{ valueText(traceRefs.payload_id) }}</code>
            </div>
            <div>
              <span>生命周期</span>
              <strong>{{ statusText(detail && detail.lifecycle_status) }}</strong>
            </div>
            <div>
              <span>幂等绑定</span>
              <strong>{{ booleanText(publication.idempotency_bound) }}</strong>
            </div>
            <div>
              <span>是否重放</span>
              <strong>{{ booleanText(publication.replayed) }}</strong>
            </div>
          </div>

          <div v-if="missingFields.length" class="missing-fields">
            <span>缺失字段</span>
            <el-tag
              v-for="field in missingFields"
              :key="field"
              size="mini"
              type="warning"
              effect="plain"
            >{{ field }}</el-tag>
          </div>
        </el-card>

        <el-collapse v-model="advancedSections" class="advanced-collapse">
          <el-collapse-item name="components">
            <template slot="title">
              <div class="collapse-title">
                <div><i class="el-icon-data-line"></i><strong>数据组件状态</strong></div>
                <span>{{ components.length }} 项</span>
              </div>
            </template>
            <el-table :data="components" size="mini" stripe empty-text="未记录组件留痕">
              <el-table-column label="组件" min-width="175">
                <template slot-scope="{ row }">
                  <div class="component-main">{{ valueText(row.requested_name || row.component_id) }}</div>
                  <code class="muted-code">{{ valueText(row.component_id) }}</code>
                </template>
              </el-table-column>
              <el-table-column label="状态" width="105">
                <template slot-scope="{ row }">
                  <el-tag :type="statusTagType(row.status)" size="mini" effect="plain">
                    {{ statusText(row.status) }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column label="已发生环节" min-width="245">
                <template slot-scope="{ row }">
                  <template v-if="activeStatePairs(row.states).length">
                    <el-tag
                      v-for="state in activeStatePairs(row.states)"
                      :key="state.key"
                      :type="state.type"
                      size="mini"
                      effect="plain"
                      class="inline-tag"
                    >{{ state.label }}</el-tag>
                  </template>
                  <span v-else class="muted">未记录</span>
                </template>
              </el-table-column>
              <el-table-column label="证据时间" min-width="175">
                <template slot-scope="{ row }">
                  <div>{{ formatBeijing(row.evidence_time || row.effective_at) }}</div>
                  <div class="muted">前视安全：{{ booleanText(row.lookahead_safe) }}</div>
                </template>
              </el-table-column>
              <el-table-column label="数据" min-width="150">
                <template slot-scope="{ row }">
                  <div>行数：{{ valueText(row.row_count) }}</div>
                  <div class="muted">缓存：{{ cacheText(row.cache) }}</div>
                </template>
              </el-table-column>
              <el-table-column label="异常说明" min-width="155">
                <template slot-scope="{ row }">{{ valueText(row.failure_reason) }}</template>
              </el-table-column>
            </el-table>
          </el-collapse-item>

          <el-collapse-item name="history">
            <template slot="title">
              <div class="collapse-title">
                <div><i class="el-icon-time"></i><strong>历史结论使用</strong></div>
                <el-tag :type="statusTagType(history.status)" size="mini" effect="plain">
                  {{ statusText(history.status) }}
                </el-tag>
              </div>
            </template>
            <div class="advanced-info-grid">
              <div><span>是否使用</span><strong>{{ booleanText(history.used) }}</strong></div>
              <div><span>候选数量</span><strong>{{ valueText(history.candidate_count) }}</strong></div>
              <div><span>PIT 截止</span><strong>{{ formatBeijing(history.pit_cutoff) }}</strong></div>
              <div><span>前视安全</span><strong>{{ booleanText(history.lookahead_safe) }}</strong></div>
              <div class="wide"><span>已用结论</span><code>{{ listText(history.used_conclusion_ids) }}</code></div>
              <div class="wide"><span>未使用原因</span><strong>{{ valueText(history.history_none_reason) }}</strong></div>
            </div>

            <el-table
              v-if="history.excluded_candidates && history.excluded_candidates.length"
              :data="history.excluded_candidates"
              size="mini"
              stripe
              class="nested-table"
            >
              <el-table-column prop="conclusion_id" label="排除结论" min-width="230" />
              <el-table-column prop="reason_code" label="排除原因" min-width="220" />
            </el-table>
          </el-collapse-item>

          <el-collapse-item name="external">
            <template slot="title">
              <div class="collapse-title">
                <div><i class="el-icon-connection"></i><strong>外部信息</strong></div>
                <el-tag :type="statusTagType(external.status)" size="mini" effect="plain">
                  {{ statusText(external.status) }}
                </el-tag>
              </div>
            </template>
            <div class="advanced-info-grid">
              <div><span>是否使用</span><strong>{{ booleanText(external.used) }}</strong></div>
              <div class="wide"><span>来源</span><strong>{{ listText(external.sources) }}</strong></div>
              <div class="wide"><span>未使用原因</span><strong>{{ valueText(external.external_none_reason) }}</strong></div>
            </div>
          </el-collapse-item>

          <el-collapse-item name="events">
            <template slot="title">
              <div class="collapse-title">
                <div><i class="el-icon-circle-check"></i><strong>安全事件</strong></div>
                <span>{{ events.length }} 项</span>
              </div>
            </template>
            <el-table :data="events" size="mini" stripe empty-text="未记录安全事件">
              <el-table-column prop="title" label="事件" min-width="180" />
              <el-table-column label="状态" width="100">
                <template slot-scope="{ row }">
                  <el-tag :type="statusTagType(row.status)" size="mini" effect="plain">
                    {{ statusText(row.status) }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column label="时间" min-width="175">
                <template slot-scope="{ row }">{{ formatBeijing(row.observed_at) }}</template>
              </el-table-column>
              <el-table-column prop="safe_ref" label="安全引用" min-width="220" />
            </el-table>
          </el-collapse-item>
        </el-collapse>
      </template>

      <div v-else-if="!loading && !error" class="empty-state">尚未读取该结论的正式留痕。</div>
    </div>

    <div slot="footer">
      <el-button @click="handleClose">关闭</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { conclusionApi } from '@/api'

const STATUS_TEXT = {
  complete: '完整',
  completed: '已完成',
  degraded: '降级可用',
  failed: '失败',
  unavailable: '不可用',
  incomplete: '未完成',
  success: '成功',
  partial_success: '部分成功',
  pending: '处理中',
  verified: '已验证',
  unverified: '未验证',
  not_recorded: '未记录',
  used: '已使用',
  unused: '未使用',
  not_used: '未使用',
  official: '正式',
  provisional: '待确认',
  formal: '正式',
  available: '可用'
}

const STATE_LABELS = {
  requested: '已请求',
  fetched: '已获取',
  visible: '已进入留痕',
  used: '已使用',
  skipped: '已跳过',
  failed: '发生失败',
  prohibited: '已禁止'
}

export default {
  name: 'FormalReceiptDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    conclusionId: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      loading: false,
      view: 'summary',
      receipt: null,
      receiptCache: {
        summary: null,
        advanced: null
      },
      loadedView: '',
      error: '',
      requestSerial: 0,
      advancedSections: ['components']
    }
  },
  computed: {
    summary() {
      return (this.receipt && this.receipt.summary) || {}
    },
    publication() {
      return (this.receipt && this.receipt.publication) || {}
    },
    detail() {
      return (this.receipt && this.receipt.detail) || null
    },
    traceRefs() {
      return (this.detail && this.detail.trace_refs) || {}
    },
    components() {
      return (this.detail && Array.isArray(this.detail.components)) ? this.detail.components : []
    },
    history() {
      return (this.detail && this.detail.history) || {}
    },
    external() {
      return (this.detail && this.detail.external) || {}
    },
    events() {
      return (this.detail && Array.isArray(this.detail.events)) ? this.detail.events : []
    },
    missingFields() {
      return Array.isArray(this.summary.missing_fields) ? this.summary.missing_fields : []
    },
    availabilityTone() {
      const indicator = String(this.summary.indicator || '').toLowerCase()
      const completeness = String(this.summary.completeness || '').toLowerCase()
      if (indicator === 'green' || ['complete', 'completed'].includes(completeness)) return 'success'
      if (indicator === 'red' || ['failed', 'unavailable', 'incomplete'].includes(completeness)) return 'danger'
      if (indicator === 'yellow' || completeness === 'degraded') return 'warning'
      return 'info'
    },
    availabilityHeadline() {
      if (this.availabilityTone === 'success') return '留痕完整'
      if (this.availabilityTone === 'warning') return '留痕降级可用'
      if (this.availabilityTone === 'danger') return '留痕未完整形成'
      return '留痕状态待确认'
    },
    availabilityDescription() {
      if (this.availabilityTone === 'success') return '数据、证据与发布回读已形成可核对记录。'
      if (this.availabilityTone === 'warning') return '部分信息未完整记录，仍可查看已经形成的留痕。'
      if (this.availabilityTone === 'danger') return '本次结论没有形成完整留痕，请重点查看发布与回读说明。'
      return '当前没有足够信息判断留痕是否完整。'
    },
    availabilityIcon() {
      if (this.availabilityTone === 'success') return 'el-icon-circle-check'
      if (this.availabilityTone === 'warning') return 'el-icon-warning-outline'
      if (this.availabilityTone === 'danger') return 'el-icon-circle-close'
      return 'el-icon-info'
    },
    availabilityTagType() {
      return {
        success: 'success',
        warning: 'warning',
        danger: 'danger',
        info: 'info'
      }[this.availabilityTone]
    },
    summaryItems() {
      const publicationStatus = this.publication.readback_status || this.publication.status
      return [
        {
          key: 'data',
          label: '分析数据',
          value: this.statusText(this.summary.data_status),
          desc: '本次结论的数据口径',
          icon: 'el-icon-data-line',
          tone: this.statusTone(this.summary.data_status)
        },
        {
          key: 'history',
          label: '历史结论',
          value: this.statusText(this.summary.history_usage),
          desc: '是否使用历史留痕',
          icon: 'el-icon-time',
          tone: this.statusTone(this.summary.history_usage)
        },
        {
          key: 'external',
          label: '外部信息',
          value: this.statusText(this.summary.external_usage),
          desc: '外部信息使用情况',
          icon: 'el-icon-connection',
          tone: this.statusTone(this.summary.external_usage)
        },
        {
          key: 'readback',
          label: '发布回读',
          value: this.statusText(publicationStatus),
          desc: '正式记录是否可回读',
          icon: 'el-icon-document-checked',
          tone: this.statusTone(publicationStatus)
        }
      ]
    },
    hasFailureReason() {
      const value = this.publication.failure_reason
      return value !== null && value !== undefined && String(value).trim() !== ''
    }
  },
  methods: {
    handleOpen() {
      this.view = 'summary'
      this.receipt = null
      this.receiptCache = { summary: null, advanced: null }
      this.loadedView = ''
      this.error = ''
      this.advancedSections = ['components']
      this.loadReceipt('summary')
    },
    handleClose() {
      this.requestSerial += 1
      this.$emit('update:visible', false)
    },
    handleTabClick(tab) {
      this.loadReceipt(tab && tab.name ? tab.name : this.view)
    },
    async loadReceipt(view) {
      const targetView = view === 'advanced' ? 'advanced' : 'summary'
      this.view = targetView

      if (!this.conclusionId) {
        this.error = '缺少结论编号，无法读取正式留痕。'
        this.receipt = null
        return
      }

      if (this.receiptCache[targetView]) {
        this.receipt = this.receiptCache[targetView]
        this.loadedView = targetView
        this.error = ''
        return
      }

      const serial = ++this.requestSerial
      this.loading = true
      this.error = ''
      this.receipt = null

      try {
        const response = await conclusionApi.getReceipt(this.conclusionId, targetView)
        if (serial !== this.requestSerial) return
        const receipt = response && response.receipt ? response.receipt : null
        if (!receipt) {
          this.error = '后端未返回可展示的正式留痕。'
          return
        }
        this.receiptCache[targetView] = receipt
        this.receipt = receipt
        this.loadedView = targetView
      } catch (error) {
        if (serial !== this.requestSerial) return
        const status = error && error.response && error.response.status
        const detail = error && error.response && error.response.data && error.response.data.detail
        const reason = this.safeReason(detail)
        if (status === 404) {
          this.error = '该结论尚未形成正式留痕。'
        } else if (status === 409) {
          this.error = reason ? '正式留痕当前不可用：' + reason : '正式留痕当前不可用。'
        } else {
          this.error = reason ? '读取正式留痕失败：' + reason : '读取正式留痕失败。'
        }
      } finally {
        if (serial === this.requestSerial) this.loading = false
      }
    },
    safeReason(value) {
      const text = typeof value === 'string' ? value.trim() : ''
      return /^[0-9A-Za-z._:-]{1,128}$/.test(text) ? text : ''
    },
    valueText(value) {
      if (value === null || value === undefined || value === '') return '未记录'
      if (value === true) return '是'
      if (value === false) return '否'
      return String(value)
    },
    booleanText(value) {
      if (value === true) return '是'
      if (value === false) return '否'
      return '未记录'
    },
    listText(value) {
      if (!Array.isArray(value) || value.length === 0) return '未记录'
      return value.map(item => {
        if (item === null || item === undefined || item === '') return '未记录'
        if (typeof item === 'object') {
          return item.safe_ref || item.source_id || item.name || '已记录'
        }
        return String(item)
      }).join('、')
    },
    statusText(value) {
      if (value === null || value === undefined || value === '') return '未记录'
      return STATUS_TEXT[String(value).toLowerCase()] || String(value)
    },
    statusTagType(value) {
      const normalized = String(value || '').toLowerCase()
      if (['success', 'completed', 'complete', 'verified', 'used', 'official', 'available'].includes(normalized)) return 'success'
      if (['failed', 'unavailable'].includes(normalized)) return 'danger'
      if (['pending', 'incomplete', 'degraded', 'unverified', 'partial_success', 'provisional'].includes(normalized)) return 'warning'
      return 'info'
    },
    statusTone(value) {
      const type = this.statusTagType(value)
      if (type === 'success') return 'green'
      if (type === 'danger') return 'red'
      if (type === 'warning') return 'amber'
      return 'gray'
    },
    activeStatePairs(states) {
      const source = states || {}
      return Object.keys(STATE_LABELS)
        .filter(key => source[key] === true)
        .map(key => ({
          key,
          label: STATE_LABELS[key],
          type: key === 'failed' || key === 'prohibited' ? 'danger' : (key === 'skipped' ? 'warning' : 'success')
        }))
    },
    cacheText(cache) {
      if (!cache || typeof cache !== 'object') return '未记录'
      if (cache.hit === true) return '命中'
      if (cache.hit === false) return '未命中'
      return '未记录'
    },
    normalizeDate(value) {
      if (!value) return null
      if (value instanceof Date) return value
      let text = String(value).trim().replace(' ', 'T')
      if (!/(Z|[+-]\d{2}:?\d{2})$/.test(text)) text += '+08:00'
      const date = new Date(text)
      return Number.isNaN(date.getTime()) ? null : date
    },
    formatBeijing(value) {
      const date = this.normalizeDate(value)
      if (!date) return '未记录'
      const parts = new Intl.DateTimeFormat('zh-CN', {
        timeZone: 'Asia/Shanghai',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }).formatToParts(date).reduce((result, item) => {
        result[item.type] = item.value
        return result
      }, {})
      return parts.year + '-' + parts.month + '-' + parts.day + ' ' + parts.hour + ':' + parts.minute + ':' + parts.second
    },
    copyConclusionId() {
      this.copyToClipboard(this.conclusionId, '结论编号已复制')
    },
    copyToClipboard(value, successMessage) {
      if (!value) return
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(value)
          .then(() => this.$message.success(successMessage || '已复制'))
          .catch(() => this.$message.info(value))
      } else {
        this.$message.info(value)
      }
    }
  }
}
</script>

<style scoped>
/deep/ .formal-receipt-dialog { max-width: 1120px; border-radius: 16px; overflow: hidden; }
/deep/ .formal-receipt-dialog .el-dialog__header { padding: 20px 22px 14px; border-bottom: 1px solid #e8eef6; }
/deep/ .formal-receipt-dialog .el-dialog__body { padding: 15px 22px 6px; }
/deep/ .formal-receipt-dialog .el-dialog__footer { padding: 12px 22px 18px; }
.receipt-topbar { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-bottom: 4px; }
.receipt-identity { min-width: 0; display: flex; align-items: center; gap: 8px; }
.receipt-identity > span { flex: 0 0 auto; color: #64748b; font-size: 12px; }
.receipt-identity code { min-width: 0; color: #1d4ed8; font-family: Consolas, Monaco, monospace; font-size: 12px; overflow-wrap: anywhere; }
.readonly-badge { flex: 0 0 auto; padding: 6px 10px; border: 1px solid #dbe7f5; border-radius: 9px; background: #f7faff; color: #64748b; font-size: 12px; }
.readonly-badge i { margin-right: 5px; color: #2563eb; }
.receipt-tabs /deep/ .el-tabs__header { margin: 7px 0 15px; }
.receipt-body { min-height: 230px; max-height: calc(86vh - 190px); overflow-y: auto; padding-right: 4px; }
.receipt-alert { margin-bottom: 14px; }

.availability-panel { display: grid; grid-template-columns: 48px minmax(0, 1fr) auto; gap: 15px; align-items: center; padding: 17px 18px; border: 1px solid #dbe7f5; border-radius: 14px; background: #f7faff; }
.availability-panel.success { border-color: #ccebd7; background: #f3fbf6; }
.availability-panel.warning { border-color: #f2dfb8; background: #fffaf0; }
.availability-panel.danger { border-color: #f2c8c8; background: #fff6f6; }
.availability-icon { width: 46px; height: 46px; border-radius: 14px; display: flex; align-items: center; justify-content: center; background: #94a3b8; color: #fff; font-size: 23px; }
.availability-panel.success .availability-icon { background: #16a34a; }
.availability-panel.warning .availability-icon { background: #f59e0b; }
.availability-panel.danger .availability-icon { background: #ef4444; }
.availability-copy span { color: #64748b; font-size: 12px; }
.availability-copy strong { display: block; margin-top: 3px; color: #0f172a; font-size: 19px; }
.availability-copy p { margin: 4px 0 0; color: #64748b; font-size: 12px; line-height: 1.5; }

.summary-status-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 11px; margin: 13px 0; }
.summary-status-item { display: flex; align-items: center; gap: 11px; min-width: 0; padding: 13px; border: 1px solid #e2e8f0; border-radius: 12px; background: #fff; }
.status-icon { flex: 0 0 34px; width: 34px; height: 34px; border-radius: 11px; display: flex; align-items: center; justify-content: center; background: #e8eef6; color: #64748b; }
.status-icon.green { background: #dcfce7; color: #15803d; }
.status-icon.amber { background: #fef3c7; color: #b45309; }
.status-icon.red { background: #fee2e2; color: #dc2626; }
.summary-status-item span { color: #64748b; font-size: 11px; }
.summary-status-item strong { display: block; margin-top: 3px; color: #0f172a; font-size: 14px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.summary-status-item p { margin: 3px 0 0; color: #94a3b8; font-size: 11px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.receipt-card { margin-bottom: 12px; border-radius: 13px; border-color: #dfe7f2; }
.receipt-card /deep/ .el-card__header { padding: 14px 16px; }
.receipt-card /deep/ .el-card__body { padding: 15px 16px; }
.card-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 14px; }
.card-header strong { color: #0f172a; font-size: 14px; }
.card-header p { margin: 4px 0 0; color: #64748b; font-size: 11px; }
.info-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; }
.info-cell, .publication-grid > div { min-width: 0; padding: 11px 12px; border-radius: 10px; background: #f8fafc; }
.info-cell span, .publication-grid span, .technical-grid span, .advanced-info-grid span, .missing-fields > span { display: block; color: #64748b; font-size: 11px; }
.info-cell strong, .publication-grid strong, .technical-grid strong, .advanced-info-grid strong { display: block; margin-top: 5px; color: #334155; font-size: 12px; line-height: 1.5; word-break: break-word; }
.publication-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
.inner-alert { margin-top: 12px; }

.technical-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
.technical-grid > div { min-width: 0; padding: 11px 12px; border-radius: 10px; background: #f8fafc; }
.technical-grid code, .advanced-info-grid code { display: block; margin-top: 5px; color: #1d4ed8; font-family: Consolas, Monaco, monospace; font-size: 11px; overflow-wrap: anywhere; }
.missing-fields { display: flex; align-items: center; flex-wrap: wrap; gap: 6px; margin-top: 12px; padding-top: 12px; border-top: 1px solid #edf2f7; }
.missing-fields > span { margin-right: 3px; }

.advanced-collapse { border: 1px solid #dfe7f2; border-radius: 13px; overflow: hidden; }
.advanced-collapse /deep/ .el-collapse-item__header { height: 50px; padding: 0 16px; border-bottom-color: #e8eef6; background: #fbfdff; }
.advanced-collapse /deep/ .el-collapse-item__wrap { border-bottom-color: #e8eef6; }
.advanced-collapse /deep/ .el-collapse-item__content { padding: 14px 16px 17px; }
.collapse-title { width: calc(100% - 24px); display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.collapse-title > div { display: flex; align-items: center; gap: 8px; }
.collapse-title i { color: #2563eb; }
.collapse-title strong { color: #334155; font-size: 13px; }
.collapse-title > span { color: #94a3b8; font-size: 11px; }
.component-main { color: #334155; font-size: 12px; }
.muted-code { display: block; margin-top: 3px; color: #94a3b8; font-family: Consolas, Monaco, monospace; font-size: 10px; overflow-wrap: anywhere; }
.inline-tag { margin: 2px 5px 2px 0; }
.muted { color: #94a3b8; font-size: 11px; }
.advanced-info-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; }
.advanced-info-grid > div { min-width: 0; padding: 11px 12px; border-radius: 10px; background: #f8fafc; }
.advanced-info-grid .wide { grid-column: span 2; }
.nested-table { margin-top: 12px; }
.empty-state { padding: 52px 0; color: #94a3b8; text-align: center; }

@media (max-width: 900px) {
  .summary-status-grid, .info-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .technical-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .advanced-info-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (max-width: 680px) {
  .receipt-topbar { align-items: flex-start; flex-direction: column; }
  .receipt-identity { align-items: flex-start; flex-wrap: wrap; }
  .availability-panel { grid-template-columns: 44px minmax(0, 1fr); }
  .availability-panel > .el-tag { grid-column: 1 / -1; width: fit-content; }
  .summary-status-grid, .info-grid, .publication-grid, .technical-grid, .advanced-info-grid { grid-template-columns: 1fr; }
  .advanced-info-grid .wide { grid-column: auto; }
  .card-header { flex-direction: column; }
}
</style>
